import React, { useState, useEffect } from "react"
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Collapse,
  Typography,
  Tooltip,
} from "@mui/material"
import {
  HomeOutlined,
  MapOutlined,
  DesktopWindowsOutlined,
  ExpandLess,
  ExpandMore,
  Storage,
  Menu,
  SensorsOutlined,
  FormatListBulletedOutlined,
  GroupOutlined,
  RecentActorsOutlined,
  SupervisorAccountOutlined,
  AccountBalanceOutlined,
  WebStoriesOutlined,
  ManageAccountsOutlined,
} from "@mui/icons-material"
import { NavLink } from "react-router-dom"
import logo from "../assets/logo/arlintas-logo.png"
import { useAuth } from "../context/AuthContext"
import { hasPermission, PERMISSIONS } from "../utils/permissions"

const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user } = useAuth()
  const [masterDataOpen, setMasterDataOpen] = useState(false)
  const [adminOpen, setAdminOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900)
      if (window.innerWidth <= 1440) {
        setIsOpen(false)
        setMasterDataOpen(false)
        setAdminOpen(false)
      } else if (window.innerWidth >= 1600) {
        setIsOpen(true)
      }
    }

    // Initial check
    handleResize()

    // Add event listener
    window.addEventListener('resize', handleResize)

    // Cleanup
    return () => window.removeEventListener('resize', handleResize)
  }, [setIsOpen])

  const toggleDrawer = () => {
    setIsOpen(!isOpen)
    if (isOpen) {
      setMasterDataOpen(false)
      setAdminOpen(false)
    }
  }

  const toggleMasterData = () => {
    setMasterDataOpen(!masterDataOpen)
    setAdminOpen(false)

    if (!masterDataOpen) {
      setIsOpen(true)
    }
  }

  const toggleAdmin = () => {
    setAdminOpen(!adminOpen)
    setMasterDataOpen(false)

    if (!adminOpen) {
      setIsOpen(true)
    }
  }

  // Helper function to check permissions
  const canAccessFeature = (feature, permission = PERMISSIONS.READ) => {
    return hasPermission(user?.permissions, feature, permission)
  }

  // Helper function to wrap items with tooltip when sidebar is closed
  const wrapWithTooltip = (element, title) => {
    if (!isOpen) {
      return (
        <Tooltip title={title} placement="right">
          {element}
        </Tooltip>
      )
    }
    return element
  }

  // Add click handler for mobile
  const handleSidebarClick = () => {
    if (window.innerWidth < 900) { // md breakpoint
      setIsOpen(false);
    }
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      sx={{
        width: isOpen ? 280 : 60,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isOpen ? 240 : 60,
          transition: "width 0.3s ease, background-color 0.3s ease",
          overflowX: "hidden",
          overflowY: "hidden",
          bgcolor: "background.default",
          border: "none",
          mx: { xs: 0, md: 1 },
          zIndex: (theme) => theme.zIndex.drawer + 1,
          // Hide scrollbar
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1, mt: 1 }}>
        {isOpen && <Box component="img" src={logo} alt="Logo" sx={{ height: 45, mx: "auto", px: 1 }} />}
        <IconButton onClick={toggleDrawer}>
          <Menu sx={{ color: "sidebar.text" }} />
        </IconButton>
      </Box>
      <List>
        {wrapWithTooltip(
          <ListItem
            button={true}
            component={NavLink}
            to="/"
            activeclassname="active"
            exact="true"
            onClick={handleSidebarClick}
            sx={{
              my: "4px",
              py: "4px",
              color: "sidebar.text",
              borderRadius: 2,
              "&.active": { bgcolor: "primary.main", color: "white" },
              "&:hover": { bgcolor: "primary.light", color: "white" },
            }}
          >
            <ListItemIcon sx={{ color: "inherit" }}>
              <HomeOutlined sx={{ color: "inherit" }} />
            </ListItemIcon>
            {isOpen && (
              <ListItemText
                primary={
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Dashboard
                  </Typography>
                }
              />
            )}
          </ListItem>,
          "Dashboard"
        )}

        {wrapWithTooltip(
          <ListItem
            button={true}
            component={NavLink}
            to="/cctv"
            activeclassname="active"
            onClick={handleSidebarClick}
            sx={{
              my: "4px",
              py: "4px",
              color: "sidebar.text",
              borderRadius: 2,
              "&.active": { bgcolor: "primary.main", color: "white" },
              "&:hover": { bgcolor: "primary.light", color: "white" },
            }}
          >
            <ListItemIcon sx={{ color: "inherit" }}>
              <DesktopWindowsOutlined sx={{ color: "inherit" }} />
            </ListItemIcon>
            {isOpen && (
              <ListItemText
                primary={
                  <Typography variant="body1"  sx={{ fontWeight: 500 }}>
                    CCTV
                  </Typography>
                }
              />
            )}
          </ListItem>,
          "CCTV"
        )}

        {wrapWithTooltip(
          <ListItem
            button={true}
            component={NavLink}
            to="/ais-monitoring"
            activeclassname="active"
            onClick={handleSidebarClick}
            sx={{
              my: "4px",
              py: "4px",
              color: "sidebar.text",
              borderRadius: 2,
              "&.active": { bgcolor: "primary.main", color: "white" },
              "&:hover": { bgcolor: "primary.light", color: "white" },
            }}
          >
            <ListItemIcon sx={{ color: "inherit" }}>
              <MapOutlined sx={{ color: "inherit" }} />
            </ListItemIcon>
            {isOpen && (
              <ListItemText
                primary={
                  <Typography variant="body1"  sx={{ fontWeight: 500 }}>
                    AIS Monitoring
                  </Typography>
                }
              />
            )}
          </ListItem>,
          "AIS Monitoring"
        )}

        {wrapWithTooltip(
          <ListItem
            button={true}
            component={NavLink}
            to="/vessel-lists"
            activeclassname="active"
            onClick={handleSidebarClick}
            sx={{
              my: "4px",
              py: "4px",
              color: "sidebar.text",
              borderRadius: 2,
              "&.active": { bgcolor: "primary.main", color: "white" },
              "&:hover": { bgcolor: "primary.light", color: "white" },
            }}
          >
            <ListItemIcon sx={{ color: "inherit" }}>
              <WebStoriesOutlined sx={{ color: "inherit", transform: 'rotate(-90deg)' }} />
            </ListItemIcon>
            {isOpen && (
              <ListItemText
                primary={
                  <Typography variant="body1"  sx={{ fontWeight: 500 }}>
                    Vessel Lists
                  </Typography>
                }
              />
            )}
          </ListItem>,
          "Vessel Lists"
        )}
      </List>

      {/* Master Data Section - Only show if user has access to any of its sub-items */}
      {(canAccessFeature('sensortype') || canAccessFeature('sensor')) && (
        <List>
          {wrapWithTooltip(
            <ListItem
              button={true}
              onClick={toggleMasterData}
              sx={{
                my: "4px",
                py: "4px",
                color: "sidebar.text",
                borderRadius: 2,
                "&.active": { bgcolor: "primary.main", color: "white" },
                "&:hover": { bgcolor: "primary.light", color: "white" },
                cursor: "pointer",
              }}
            >
              <ListItemIcon sx={{ color: "inherit" }}>
                <Storage sx={{ color: "inherit" }} />
              </ListItemIcon>
              {isOpen && (
                <ListItemText
                  primary={
                    <Typography variant="body1"  sx={{ fontWeight: 500 }}>
                      Master Data
                    </Typography>
                  }
                />
              )}
              {isOpen && (masterDataOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItem>,
            "Master Data"
          )}
          <Collapse in={masterDataOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {canAccessFeature('sensortype') && (
                <ListItem
                  button={true}
                  component={NavLink}
                  to="/sensor-types"
                  activeclassname="active"
                  sx={{
                    my: "4px",
                    py: "4px",
                    pl: 4,
                    color: "sidebar.text",
                    borderRadius: 2,
                    "&.active": { bgcolor: "primary.main", color: "white" },
                    "&:hover": { bgcolor: "primary.light", color: "white" },
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit" }}>
                    <FormatListBulletedOutlined sx={{ color: "inherit" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1"  sx={{ fontWeight: 500 }}>
                        Sensor Types
                      </Typography>
                    }
                  />
                </ListItem>
              )}
              {canAccessFeature('sensor') && (
                <ListItem
                  button={true}
                  component={NavLink}
                  to="/sensors"
                  activeclassname="active"
                  sx={{
                    my: "4px",
                    py: "4px",
                    pl: 4,
                    color: "sidebar.text",
                    borderRadius: 2,
                    "&.active": { bgcolor: "primary.main", color: "white" },
                    "&:hover": { bgcolor: "primary.light", color: "white" },
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit" }}>
                    <SensorsOutlined sx={{ color: "inherit" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1"  sx={{ fontWeight: 500 }}>
                        Sensors
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Collapse>
        </List>
      )}

      {/* Administration Section - Only show if user has access to any of its sub-items */}
      {(canAccessFeature('user') || canAccessFeature('role')) && (
        <List>
          {wrapWithTooltip(
            <ListItem
              button={true}
              onClick={toggleAdmin}
              sx={{
                my: "4px",
                py: "4px",
                color: "sidebar.text",
                borderRadius: 2,
                "&.active": { bgcolor: "primary.main", color: "white" },
                "&:hover": { bgcolor: "primary.light", color: "white" },
                cursor: "pointer",
              }}
            >
              <ListItemIcon sx={{ color: "inherit" }}>
                <GroupOutlined sx={{ color: "inherit" }} />
              </ListItemIcon>
              {isOpen && (
                <ListItemText
                  primary={
                    <Typography variant="body1"  sx={{ fontWeight: 500 }}>
                      Administration
                    </Typography>
                  }
                />
              )}
              {isOpen && (adminOpen ? <ExpandLess sx={{ color: "inherit" }} /> : <ExpandMore sx={{ color: "inherit" }} />)}
            </ListItem>,
            "Administration"
          )}
          <Collapse in={adminOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {canAccessFeature('user') && (
                <ListItem
                  button={true}
                  component={NavLink}
                  to="/users"
                  activeclassname="active"
                  sx={{
                    my: "4px",
                    py: "4px",
                    pl: 4,
                    color: "sidebar.text",
                    borderRadius: 2,
                    "&.active": { bgcolor: "primary.main", color: "white" },
                    "&:hover": { bgcolor: "primary.light", color: "white" },
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit" }}>
                    <RecentActorsOutlined sx={{ color: "inherit" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1"  sx={{ fontWeight: 500 }}>
                        Users
                      </Typography>
                    }
                  />
                </ListItem>
              )}
              {canAccessFeature('role') && (
                <ListItem
                  button={true}
                  component={NavLink}
                  to="/roles"
                  activeclassname="active"
                  sx={{
                    my: "4px",
                    py: "4px",
                    pl: 4,
                    color: "sidebar.text",
                    borderRadius: 2,
                    "&.active": { bgcolor: "primary.main", color: "white" },
                    "&:hover": { bgcolor: "primary.light", color: "white" },
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit" }}>
                    <SupervisorAccountOutlined sx={{ color: "inherit" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1"  sx={{ fontWeight: 500 }}>
                        Roles
                      </Typography>
                    }
                  />
                </ListItem>
              )}
              {(canAccessFeature('client', PERMISSIONS.ALL) && canAccessFeature('user', PERMISSIONS.ALL)) && (
                <ListItem
                  button={true}
                  component={NavLink}
                  to="/client-registration"
                  activeclassname="active"
                  sx={{
                    my: "4px",
                    py: "4px",
                    pl: 4,
                    color: "sidebar.text",
                    borderRadius: 2,
                    "&.active": { bgcolor: "primary.main", color: "white" },
                    "&:hover": { bgcolor: "primary.light", color: "white" },
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit" }}>
                    <AccountBalanceOutlined sx={{ color: "inherit" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1"  sx={{ fontWeight: 500 }}>
                        Client Registration
                      </Typography>
                    }
                  />
                </ListItem>
              )}
              {(canAccessFeature('client', PERMISSIONS.ALL) && canAccessFeature('user', PERMISSIONS.ALL)) && (
                <ListItem
                  button={true}
                  component={NavLink}
                  to="/client-management"
                  activeclassname="active"
                  sx={{
                    my: "4px",
                    py: "4px",
                    pl: 4,
                    color: "sidebar.text",
                    borderRadius: 2,
                    "&.active": { bgcolor: "primary.main", color: "white" },
                    "&:hover": { bgcolor: "primary.light", color: "white" },
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit" }}>
                    <ManageAccountsOutlined sx={{ color: "inherit" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Client Management
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Collapse>
        </List>
      )}
    </Drawer>
  )
}

export default Sidebar
